import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
const ImageComparison = ({
  beforeImage,
  afterImage,
  initialAspectRatio = 16 / 9,
  className = '',
  maxWidthPx = 960,
  minWidthPx = 320,
  maxHeightVh = 80,
  smallScreenMaxHeightVh = 65,
  largeScreenBreakpointPx = 1280,
  // 新增：平板（中屏）粒度控制
  mediumScreenMaxHeightVh = 68,
  mediumScreenBreakpointPx = 1024,
  maxWidthPxMd = 900
}) => {
  const {t} = useTranslation();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio); // 默认宽高比
  const [containerStyle, setContainerStyle] = useState({});
  const [, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [beforeImageLoaded, setBeforeImageLoaded] = useState(false);
  const [imgKey, setImgKey] = useState(0);
  const handleBeforeImageLoad = useCallback((e) => {

    const { naturalWidth, naturalHeight } = e.target;
    const ratio = naturalWidth / naturalHeight;
    setAspectRatio(ratio);
    setBeforeImageLoaded(true);
  }, []);
  const handleImageLoad = useCallback((e) => {
    setImageLoaded(true);
  }, []);

  useEffect(() => {
    setImageLoaded(false);
    setImgKey(prevKey => prevKey + 1);
  }, [afterImage]);

  // 计算容器样式 - 兼顾原图长宽比和屏幕自适应
  const calculateContainerStyle = useCallback(() => {
    if (!containerRef.current || aspectRatio <= 0) {
      return { width: '100%', height: 'auto' };
    }

    const parentElement = containerRef.current.parentElement;
    if (!parentElement) {
      return { width: '100%', height: 'auto' };
    }

    // 获取父容器的可用空间
    const parentRect = parentElement.getBoundingClientRect();
    const parentWidth = parentRect.width;
    const winW = window.innerWidth;
    const isLargeScreen = winW >= largeScreenBreakpointPx;
    const isMediumScreen = winW >= mediumScreenBreakpointPx && winW < largeScreenBreakpointPx;
    const appliedMaxHeightVh = isLargeScreen
      ? maxHeightVh
      : isMediumScreen
      ? mediumScreenMaxHeightVh
      : smallScreenMaxHeightVh;
    const parentHeight = window.innerHeight * (appliedMaxHeightVh / 100); // 限制最大高度为可配置的视口百分比

    // 计算基于长宽比的理想尺寸
    const idealHeight = parentWidth / aspectRatio;

    let finalWidth, finalHeight;

    // 预计算绝对最大宽度（考虑父容器宽度、视口宽度与配置最大宽度）；按屏幕分级限制宽度
    const widthCap = isLargeScreen ? maxWidthPx : isMediumScreen ? maxWidthPxMd : Number.POSITIVE_INFINITY;
    const absoluteMaxWidth = Math.min(parentWidth, window.innerWidth * 0.95, widthCap);

    // 根据父容器空间选择最适合的尺寸
    if (idealHeight <= parentHeight) {
      // 宽度优先：使用父容器的宽度，高度根据长宽比计算
      finalWidth = absoluteMaxWidth; // 最大不超过配置和视口
      finalHeight = finalWidth / aspectRatio;
    } else {
      // 高度优先：使用限制的高度，宽度根据长宽比计算
      finalHeight = parentHeight;
      finalWidth = finalHeight * aspectRatio;

      // 确保宽度不超过绝对最大宽度
      if (finalWidth > absoluteMaxWidth) {
        finalWidth = absoluteMaxWidth;
        finalHeight = finalWidth / aspectRatio;
      }

      // 确保宽度不超过父容器
      if (finalWidth > parentWidth) {
        finalWidth = parentWidth;
        finalHeight = finalWidth / aspectRatio;
      }
    }

    // 确保最小尺寸
    const minWidth = minWidthPx;
    const minHeight = minWidth / aspectRatio;

    if (finalWidth < minWidth) {
      finalWidth = minWidth;
      finalHeight = minHeight;
    }

    return {
      width: `${Math.round(finalWidth)}px`,
      height: `${Math.round(finalHeight)}px`,
      maxWidth: '100%',
      // 只在初始化后添加过渡效果，避免首次渲染时的动画
      ...(isInitialized && {
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      })
    };
  }, [
    aspectRatio,
    isInitialized,
    maxWidthPx,
    maxWidthPxMd,
    minWidthPx,
    maxHeightVh,
    mediumScreenMaxHeightVh,
    smallScreenMaxHeightVh,
    mediumScreenBreakpointPx,
    largeScreenBreakpointPx
  ]);

  // 初始化容器尺寸
  React.useEffect(() => {
    if (containerRef.current && aspectRatio > 0) {
      // 延迟一帧确保DOM已完全渲染
      requestAnimationFrame(() => {
        const newStyle = calculateContainerStyle();
        setContainerStyle(newStyle);
        // 标记为已初始化，后续变化将有过渡动画
        setTimeout(() => setIsInitialized(true), 100);
      });
    }
  }, [aspectRatio, calculateContainerStyle]);

  // 更新容器样式
  React.useEffect(() => {
    const newStyle = calculateContainerStyle();
    setContainerStyle(newStyle);
  }, [calculateContainerStyle]);

  // 使用ResizeObserver监听父容器大小变化
  React.useEffect(() => {
    if (!containerRef.current) return;

    const parentElement = containerRef.current.parentElement;
    if (!parentElement) return;

    // 创建ResizeObserver监听父容器大小变化
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerDimensions({ width, height });
      }
    });

    resizeObserverRef.current.observe(parentElement);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // 监听窗口大小变化（用于视口相关的计算）
  React.useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      // 使用防抖来避免频繁计算
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newStyle = calculateContainerStyle();
        setContainerStyle(newStyle);
      }, 16); // 约60fps的更新频率
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [calculateContainerStyle]);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
    e.stopPropagation();
    // 禁用页面滚动
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    // 阻止默认行为和事件冒泡
    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    // 恢复页面滚动
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // 组件卸载时确保恢复页面滚动
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);


  return (
    <motion.div
      ref={containerRef}
      className={`image-comparison-container relative mx-auto rounded-lg overflow-hidden shadow-2xl ${className}`}
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* 修图后的图片 (背景) */}
      <div className="absolute inset-0">
        <img
          key={imgKey}
          src={afterImage}
          alt="修图后"
          className="w-full h-full object-cover"
          draggable={false}
          onLoad={handleImageLoad}
        />
      </div>

      {/* 原图 (前景，被裁剪) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={beforeImage}
          alt="原图"
          className="w-full h-full object-cover"
          draggable={false}
          onLoad={handleBeforeImageLoad}
        />
      </div>

      {/* 滑块 - 扩大可拖拽区域 */}
      <motion.div
        className="absolute top-0 bottom-0 flex items-center justify-center cursor-ew-resize z-10"
        style={{
          left: `calc(${sliderPosition}% - 20px)`,
          width: '40px' // 扩大可点击区域
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        whileHover={{ scale: 1.05 }}
      >
        {/* 实际的滑块线条 */}
        <div
          className="w-0.5 h-full bg-white relative"
          style={{
            boxShadow: isDragging
              ? "0 0 15px rgba(255, 255, 255, 0.6)"
              : "0 0 8px rgba(255, 255, 255, 0.3)"
          }}
        >
          {/* 中心圆点 */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full"
          >
          </div>
        </div>
      </motion.div>

      {/* 标签 - 与中央圆球同一水平线，紧挨竖线 */}
      <div 
        className={`absolute top-1/2 transform -translate-y-1/2 text-gray-100/30 px-3 py-1 text-sm font-bold ${!isDragging ? 'transition-all duration-200' : ''}`}
        style={{
          right: `calc(${100 - sliderPosition}% + 12px)`, // Before标签右侧紧挨竖线，增加间距避免与圆球重叠
        }}
      >
        Before
      </div>
      <div 
        className={`absolute top-1/2 transform -translate-y-1/2 text-gray-100/30 px-3 py-1 text-sm font-bold ${!isDragging ? 'transition-all duration-200' : ''}`}
        style={{
          left: `calc(${sliderPosition}% + 12px)`, // After标签左侧紧挨竖线，增加间距避免与圆球重叠
        }}
      >
        After
      </div>

      {/* 拖拽提示 - 左侧紧挨竖线 */}
      {!isDragging && imageLoaded && beforeImageLoaded && (
        <motion.div
          className="absolute bottom-4 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm"
          style={{
            left: `calc(${sliderPosition}% + 4px)` // 拖拽提示左侧紧挨竖线
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          {t('dragToCompare')}
        </motion.div>
      )}

      {/* 加载状态 */}
      {(!imageLoaded || !beforeImageLoaded || !imageLoaded) && (
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 bg-opacity-95"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Loading 动画 - 三个点跳动 */}
          <div className="flex items-center justify-center space-x-2 mb-4">
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                className="w-3 h-3 rounded-full bg-gradient-to-r from-pink-400 to-pink-500"
                animate={{
                  y: [0, -12, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: index * 0.2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ImageComparison;